import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_FIELDS, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * OrganizationsApiService (Angular 18) — organizations, addresses, contacts,
 * and corporate memberships.
 *
 * Replaces apps/b2b-ui/common/services/api/organizationsApi/organizationsApi.service.js.
 *
 * Three sub-namespaces:
 *   apiService.organizations.addresses.{list,create,update,remove,detail,primary}
 *   apiService.organizations.contacts.{list,detail,create,update,remove,primary}
 *   apiService.organizations.membership.{list,cardholders,detail,attributes,
 *                                         create,update,remove}
 *
 * Legacy quirks preserved:
 *   - `search()`, `list()`, `listTransactable()` all force a trailing
 *     `search=` parameter when not provided (matches the `userProxy.search`
 *     / `price.types.search` matchall trick).
 *   - `addresses.create/update` resolve `isPrimary` from `props.isPrimary`
 *     (if the key is present) otherwise from `props.address.isPrimary`.
 *   - `membership.create()` and `.update()` send near-empty bodies — the
 *     legacy had a `// TODO: Populate` note. Preserved 1:1.
 *   - `membership.attributes()` GETs `/organization/integration/<id>/attributes`
 *     under `endpoint`, NOT under the `membership.service` path.
 */
const ORGANIZATION_BASE_V2 = '/v2/api/organization';
const MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2 = '/v2/api/membership/corporate/organizations';
const ORGANIZATION_INTEGRATION_BASE_V2 = '/v2/api/organization/integration';

const NAMES_SEARCH_FIELDS = ['search', 'page', 'pageSize', 'sortDirection', 'sortField', 'totalItems'] as const;

const SEARCH_ORGS_FIELDS = [
  'status',
  'page',
  'pageSize',
  'sortDirection',
  'sortField',
  'search',
  'type',
  'category',
  'organizationName',
  'contactName',
] as const;

const TRANSACTABLE_FIELDS = ['pageSize', 'search', ...PAGINATION_FIELDS.filter((f) => f !== 'pageSize')] as const;

const ORG_LIST_FIELDS = ['search', 'page', 'pageSize', 'sortDirection', 'sortField', 'withAddress'] as const;

const ADDRESS_LIST_FIELDS = ['type', 'admissionType', ...PAGINATION_SEARCH_FIELDS] as const;

interface OrganizationAddressPayload {
  organizationId: number | string;
  isPrimary?: boolean;
  address: {
    id?: number | string;
    country?: string;
    streetAddress1?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    type?: string;
    isPrimary?: boolean;
  };
}

interface OrganizationContactPayload {
  id?: number | string;
  organizationId: number | string;
  contactId?: number | string;
  addressId?: number | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  country?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  type?: string;
  primary?: boolean;
  isPrimary?: boolean;
}

function withSearchSentinel(props: Record<string, unknown> | undefined, fields: readonly string[]) {
  let params = buildParams(props, fields);
  if (!params.has('search')) {
    params = params.set('search', '');
  }
  return params;
}

@Injectable({ providedIn: 'root' })
export class OrganizationsApiService {
  private readonly http = inject(HttpClient);

  search(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/names`, {
        params: withSearchSentinel(props, NAMES_SEARCH_FIELDS),
      }),
    );
  }

  searchOrganizations(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/search`, {
        params: buildParams(props, SEARCH_ORGS_FIELDS),
      }),
    );
  }

  listTransactable(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/transactable`, {
        params: withSearchSentinel(props, TRANSACTABLE_FIELDS),
      }),
    );
  }

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(ORGANIZATION_BASE_V2, {
        params: withSearchSentinel(props, ORG_LIST_FIELDS),
      }),
    );
  }

  readonly addresses = {
    list: (props: { organizationId: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/address`, {
          params: buildParams(props, ADDRESS_LIST_FIELDS),
        }),
      ),

    create: (props: OrganizationAddressPayload): Promise<AjsResponse<unknown>> => {
      const isPrimary = Object.prototype.hasOwnProperty.call(props, 'isPrimary')
        ? props.isPrimary
        : props.address.isPrimary;
      return wrapAjs(
        this.http.post<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/address`, {
          country: props.address.country,
          streetAddress1: props.address.streetAddress1,
          streetAddress2: props.address.streetAddress2,
          city: props.address.city,
          state: props.address.state,
          zipCode: props.address.zipCode,
          type: props.address.type,
          isPrimary,
        }),
      );
    },

    update: (props: OrganizationAddressPayload): Promise<AjsResponse<unknown>> => {
      const isPrimary = Object.prototype.hasOwnProperty.call(props, 'isPrimary')
        ? props.isPrimary
        : props.address.isPrimary;
      return wrapAjs(
        this.http.put<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/address`, {
          id: props.address.id,
          country: props.address.country,
          streetAddress1: props.address.streetAddress1,
          streetAddress2: props.address.streetAddress2,
          city: props.address.city,
          state: props.address.state,
          zipCode: props.address.zipCode,
          type: props.address.type,
          isPrimary,
        }),
      );
    },

    remove: (props: { organizationId: number | string; id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/address/${props.id}`)),

    detail: (props: { organizationId: number | string; id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/address/${props.id}`)),

    primary: (props: { organizationId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/address/primary`)),
  };

  readonly contacts = {
    list: (props: { organizationId: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/contact`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (props: { organizationId: number | string; contactId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/contact/${props.contactId}`)),

    create: (props: OrganizationContactPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/contact`, {
          addressId: props.addressId,
          accountId: props.organizationId,
          organizationId: props.organizationId,
          firstName: props.firstName,
          lastName: props.lastName,
          email: props.email,
          phoneNumber: props.phoneNumber,
          country: props.country,
          streetAddress1: props.streetAddress1,
          streetAddress2: props.streetAddress2,
          city: props.city,
          state: props.state,
          zipCode: props.zipCode,
          type: props.type,
          primary: props.primary,
          isPrimary: props.isPrimary,
        }),
      ),

    update: (props: OrganizationContactPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/contact`, {
          id: props.id,
          addressId: props.addressId,
          accountId: props.organizationId,
          organizationId: props.organizationId,
          firstName: props.firstName,
          lastName: props.lastName,
          email: props.email,
          phoneNumber: props.phoneNumber,
          country: props.country,
          streetAddress1: props.streetAddress1,
          streetAddress2: props.streetAddress2,
          city: props.city,
          state: props.state,
          zipCode: props.zipCode,
          type: props.type,
          primary: props.primary,
          isPrimary: props.isPrimary,
        }),
      ),

    remove: (props: { organizationId: number | string; contactId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/contact/${props.contactId}`)),

    primary: (props: { organizationId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ORGANIZATION_BASE_V2}/${props.organizationId}/contact/primary`)),
  };

  readonly membership = {
    list: (props: { id: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2}/${props.id}`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    cardholders: (props: { id: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2}/${props.id}/cards`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2}/${props.id}`)),

    attributes: (props: { id: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ORGANIZATION_INTEGRATION_BASE_V2}/${props.id}/attributes`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    // Legacy `// TODO: Populate` — preserved as empty body.
    create: (_props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2, {})),

    update: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2, {
          id: props.id,
        }),
      ),

    remove: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${MEMBERSHIP_CORPORATE_ORGANIZATIONS_BASE_V2}/${props.id}`)),
  };
}
